import { Component, Inject, ViewChild, ElementRef, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { PdfViewerService } from './services/pdf-viewer.service';
import { CoordinateConverterService, CanvasCoordinates } from './services/coordinate-converter.service';

export interface SignatureSelection {
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
    canvasX: number;
    canvasY: number;
    canvasWidth: number;
    canvasHeight: number;
}

interface SelectionRectangle {
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    isDrawing: boolean;
}

@Component({
    selector: 'app-signature-position-modal',
    templateUrl: './signature-position-modal.component.html',
    styleUrls: ['./signature-position-modal.component.scss']
})
export class SignaturePositionModalComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('pdfCanvas', { static: false }) pdfCanvasRef?: ElementRef<HTMLCanvasElement>;
    @ViewChild('overlayCanvas', { static: false }) overlayCanvasRef?: ElementRef<HTMLCanvasElement>;

    // PDF state
    currentDocument: PDFDocumentProxy | null = null;
    currentPage: PDFPageProxy | null = null;
    currentPageNumber: number = 1;
    totalPages: number = 0;
    currentScale: number = 1.2;

    // Selection state
    selection: SelectionRectangle = {
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        isDrawing: false
    };
    selectedArea: SignatureSelection | null = null;

    // UI state
    isLoading: boolean = false;
    errorMessage: string = '';
    warningMessage: string = '';
    minimumSelectionSize: number = 30; // pixels

    // Selection mode: 'click' = Click to Place, 'draw' = Draw Custom
    selectionMode: 'click' | 'draw' = 'click';

    // Default dimensions for Click to Place mode (in PDF points)
    defaultWidth: number = 200;
    defaultHeight: number = 70;

    // Drag-to-move state
    isDragging: boolean = false;
    dragOffsetX: number = 0;
    dragOffsetY: number = 0;

    private destroy$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: { documentUrl: string, documentId: number, defaultWidth?: number, defaultHeight?: number },
        private dialogRef: MatDialogRef<SignaturePositionModalComponent>,
        private pdfViewerService: PdfViewerService,
        private coordinateConverterService: CoordinateConverterService
    ) {}

    ngOnInit(): void {
        // Apply custom default dimensions if provided
        if (this.data.defaultWidth) {
            this.defaultWidth = this.data.defaultWidth;
        }
        if (this.data.defaultHeight) {
            this.defaultHeight = this.data.defaultHeight;
        }

        if (this.data.documentUrl) {
            this.loadDocument();
        }
    }

    ngAfterViewInit(): void {
        // Setup will happen after document loads
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.pdfViewerService.cleanup();
    }

    /**
     * Load PDF document
     */
    loadDocument(): void {
        this.isLoading = true;
        this.errorMessage = '';

        this.pdfViewerService.loadDocument(this.data.documentUrl)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (pdf: PDFDocumentProxy) => {
                    this.currentDocument = pdf;
                    this.totalPages = pdf.numPages;
                    this.renderCurrentPage();
                },
                error: (err: Error) => {
                    this.isLoading = false;
                    this.errorMessage = err.message || 'Eroare la incarcarea documentului';
                }
            });
    }

    /**
     * Render current page
     */
    renderCurrentPage(): void {
        if (!this.currentDocument) {
            return;
        }

        this.pdfViewerService.getPage(this.currentPageNumber)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (page: PDFPageProxy) => {
                    this.currentPage = page;
                    this.renderPage(page);
                },
                error: (err: Error) => {
                    this.errorMessage = err.message;
                    this.isLoading = false;
                }
            });
    }

    /**
     * Render page on canvas
     */
    renderPage(page: PDFPageProxy): void {
        const canvas = this.pdfCanvasRef?.nativeElement;

        if (!canvas) {
            setTimeout(() => this.renderPage(page), 100);
            return;
        }

        this.pdfViewerService.renderPage(page, canvas, this.currentScale)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    this.syncOverlayCanvas();
                    this.setupOverlayCanvas();
                },
                error: (err: Error) => {
                    this.isLoading = false;
                    this.errorMessage = err.message;
                }
            });
    }

    /**
     * Navigate to previous page
     */
    previousPage(): void {
        if (this.currentPageNumber > 1) {
            this.currentPageNumber--;
            this.clearSelection();
            this.renderCurrentPage();
        }
    }

    /**
     * Navigate to next page
     */
    nextPage(): void {
        if (this.currentPageNumber < this.totalPages) {
            this.currentPageNumber++;
            this.clearSelection();
            this.renderCurrentPage();
        }
    }

    /**
     * Zoom in
     */
    zoomIn(): void {
        if (this.currentScale < 3.0) {
            this.currentScale += 0.2;
            this.clearSelection();
            this.renderCurrentPage();
        }
    }

    /**
     * Zoom out
     */
    zoomOut(): void {
        if (this.currentScale > 0.5) {
            this.currentScale -= 0.2;
            this.clearSelection();
            this.renderCurrentPage();
        }
    }

    /**
     * Setup overlay canvas
     */
    setupOverlayCanvas(): void {
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas) return;

        // Remove old listeners to prevent duplicates
        canvas.removeEventListener('mousedown', this.onMouseDown.bind(this));
        canvas.removeEventListener('mousemove', this.onMouseMove.bind(this));
        canvas.removeEventListener('mouseup', this.onMouseUp.bind(this));
        canvas.removeEventListener('mouseleave', this.onMouseLeave.bind(this));

        // Add new listeners
        canvas.addEventListener('mousedown', this.boundMouseDown);
        canvas.addEventListener('mousemove', this.boundMouseMove);
        canvas.addEventListener('mouseup', this.boundMouseUp);
        canvas.addEventListener('mouseleave', this.boundMouseLeave);
    }

    // Bound event handlers
    private boundMouseDown = (e: MouseEvent) => this.onMouseDown(e);
    private boundMouseMove = (e: MouseEvent) => this.onMouseMove(e);
    private boundMouseUp = (e: MouseEvent) => this.onMouseUp(e);
    private boundMouseLeave = (e: MouseEvent) => this.onMouseLeave(e);

    /**
     * Sync overlay canvas with PDF canvas
     */
    syncOverlayCanvas(): void {
        const overlayCanvas = this.overlayCanvasRef?.nativeElement;
        const pdfCanvas = this.pdfCanvasRef?.nativeElement;

        if (!overlayCanvas || !pdfCanvas || !this.currentPage) {
            return;
        }

        const viewport = this.currentPage.getViewport({ scale: this.currentScale });

        // Set canvas internal dimensions
        overlayCanvas.width = pdfCanvas.width;
        overlayCanvas.height = pdfCanvas.height;

        // Set canvas display dimensions to match PDF canvas
        overlayCanvas.style.width = pdfCanvas.width + 'px';
        overlayCanvas.style.height = pdfCanvas.height + 'px';
    }

    /**
     * Mouse down handler - supports both Click to Place and Draw modes
     */
    onMouseDown(event: MouseEvent): void {
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas) return;

        const pos = this.coordinateConverterService.getMousePosition(event, canvas);
        this.warningMessage = '';

        if (this.selectionMode === 'click') {
            // Click to Place Mode
            if (this.selectedArea && this.isPointInSelection(pos.x, pos.y)) {
                // Click on existing selection - start drag to move
                this.isDragging = true;
                this.dragOffsetX = pos.x - this.selectedArea.canvasX;
                this.dragOffsetY = pos.y - this.selectedArea.canvasY;
                canvas.style.cursor = 'grabbing';
            } else {
                // New click - place default rectangle centered on cursor
                this.placeDefaultRectangle(pos.x, pos.y);
            }
        } else {
            // Draw Custom Mode (existing behavior)
            this.selection.startX = pos.x;
            this.selection.startY = pos.y;
            this.selection.currentX = pos.x;
            this.selection.currentY = pos.y;
            this.selection.isDrawing = true;
        }
    }

    /**
     * Mouse move handler - supports both modes
     */
    onMouseMove(event: MouseEvent): void {
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas) return;

        const pos = this.coordinateConverterService.getMousePosition(event, canvas);

        if (this.selectionMode === 'click') {
            if (this.isDragging && this.selectedArea) {
                // Drag to move
                this.moveSelection(pos.x - this.dragOffsetX, pos.y - this.dragOffsetY);
            } else {
                // Update cursor based on hover position
                this.updateCursor(pos.x, pos.y);
            }
        } else {
            // Draw mode (existing behavior)
            if (this.selection.isDrawing) {
                this.selection.currentX = pos.x;
                this.selection.currentY = pos.y;
                this.drawSelectionRectangle();
            }
        }
    }

    /**
     * Mouse up handler
     */
    onMouseUp(event: MouseEvent): void {
        const canvas = this.overlayCanvasRef?.nativeElement;

        if (this.selectionMode === 'click') {
            if (this.isDragging) {
                this.isDragging = false;
                if (canvas) {
                    this.updateCursor(
                        this.coordinateConverterService.getMousePosition(event, canvas).x,
                        this.coordinateConverterService.getMousePosition(event, canvas).y
                    );
                }
            }
        } else {
            // Draw mode - finalize (existing behavior)
            if (this.selection.isDrawing) {
                this.selection.isDrawing = false;
                this.finalizeSelection();
            }
        }
    }

    /**
     * Mouse leave handler
     */
    onMouseLeave(event: MouseEvent): void {
        const canvas = this.overlayCanvasRef?.nativeElement;

        if (this.selectionMode === 'click') {
            if (this.isDragging) {
                this.isDragging = false;
            }
            if (canvas) {
                canvas.style.cursor = 'crosshair';
            }
        } else {
            if (this.selection.isDrawing) {
                this.selection.isDrawing = false;
                this.clearOverlayCanvas();
            }
        }
    }

    /**
     * Draw selection rectangle
     */
    drawSelectionRectangle(): void {
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        this.clearOverlayCanvas();

        const x = Math.min(this.selection.startX, this.selection.currentX);
        const y = Math.min(this.selection.startY, this.selection.currentY);
        const width = Math.abs(this.selection.currentX - this.selection.startX);
        const height = Math.abs(this.selection.currentY - this.selection.startY);

        // Draw dashed border
        ctx.strokeStyle = '#0d6efd';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(x, y, width, height);

        // Fill with semi-transparent blue
        ctx.fillStyle = 'rgba(13, 110, 253, 0.15)';
        ctx.fillRect(x, y, width, height);

        ctx.setLineDash([]);
    }

    /**
     * Clear overlay canvas
     */
    clearOverlayCanvas(): void {
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    /**
     * Finalize selection
     */
    finalizeSelection(): void {
        const x = Math.min(this.selection.startX, this.selection.currentX);
        const y = Math.min(this.selection.startY, this.selection.currentY);
        const width = Math.abs(this.selection.currentX - this.selection.startX);
        const height = Math.abs(this.selection.currentY - this.selection.startY);

        // Validate minimum size
        if (width < this.minimumSelectionSize || height < this.minimumSelectionSize) {
            this.warningMessage = `Selectia este prea mica. Dimensiunea minima este ${this.minimumSelectionSize}x${this.minimumSelectionSize} pixeli.`;
            this.clearOverlayCanvas();
            return;
        }

        const canvasCoords: CanvasCoordinates = { x, y, width, height };

        // Convert to PDF coordinates
        if (this.currentPage) {
            const viewport = this.currentPage.getViewport({ scale: this.currentScale });
            const pdfCoords = this.coordinateConverterService.canvasToPdf(
                canvasCoords,
                viewport.height / this.currentScale,
                this.currentScale
            );

            this.selectedArea = {
                pageNumber: this.currentPageNumber,
                x: Math.round(pdfCoords.x),
                y: Math.round(pdfCoords.y),
                width: Math.round(pdfCoords.width),
                height: Math.round(pdfCoords.height),
                canvasX: x,
                canvasY: y,
                canvasWidth: width,
                canvasHeight: height
            };
        }

        this.drawSelectionRectangle();
    }

    /**
     * Clear selection
     */
    clearSelection(): void {
        this.selectedArea = null;
        this.warningMessage = '';
        this.isDragging = false;
        this.clearOverlayCanvas();
    }

    // ==================== CLICK TO PLACE METHODS ====================

    /**
     * Place default rectangle centered on click position
     */
    placeDefaultRectangle(clickX: number, clickY: number): void {
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas || !this.currentPage) return;

        // Convert default dimensions from PDF points to canvas pixels
        const canvasWidth = this.defaultWidth * this.currentScale;
        const canvasHeight = this.defaultHeight * this.currentScale;

        // Center on click position
        let canvasX = clickX - canvasWidth / 2;
        let canvasY = clickY - canvasHeight / 2;

        // Constrain to canvas bounds
        canvasX = Math.max(0, Math.min(canvasX, canvas.width - canvasWidth));
        canvasY = Math.max(0, Math.min(canvasY, canvas.height - canvasHeight));

        // Create selection
        this.createSelectionFromCanvas(canvasX, canvasY, canvasWidth, canvasHeight);
        this.drawFinalSelection();
    }

    /**
     * Move existing selection to new position
     */
    moveSelection(newX: number, newY: number): void {
        if (!this.selectedArea) return;

        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas) return;

        const width = this.selectedArea.canvasWidth;
        const height = this.selectedArea.canvasHeight;

        // Constrain to canvas bounds
        newX = Math.max(0, Math.min(newX, canvas.width - width));
        newY = Math.max(0, Math.min(newY, canvas.height - height));

        // Update selection
        this.createSelectionFromCanvas(newX, newY, width, height);
        this.drawFinalSelection();
    }

    /**
     * Check if a point is inside the current selection
     */
    isPointInSelection(x: number, y: number): boolean {
        if (!this.selectedArea) return false;

        return x >= this.selectedArea.canvasX &&
               x <= this.selectedArea.canvasX + this.selectedArea.canvasWidth &&
               y >= this.selectedArea.canvasY &&
               y <= this.selectedArea.canvasY + this.selectedArea.canvasHeight;
    }

    /**
     * Create selection from canvas coordinates and convert to PDF coordinates
     */
    createSelectionFromCanvas(canvasX: number, canvasY: number,
                              canvasWidth: number, canvasHeight: number): void {
        if (!this.currentPage) return;

        const viewport = this.currentPage.getViewport({ scale: this.currentScale });
        const pageHeight = viewport.height / this.currentScale;

        // Convert to PDF coordinates
        const pdfCoords = this.coordinateConverterService.canvasToPdf(
            { x: canvasX, y: canvasY, width: canvasWidth, height: canvasHeight },
            pageHeight,
            this.currentScale
        );

        this.selectedArea = {
            pageNumber: this.currentPageNumber,
            x: Math.round(pdfCoords.x),
            y: Math.round(pdfCoords.y),
            width: Math.round(pdfCoords.width),
            height: Math.round(pdfCoords.height),
            canvasX: canvasX,
            canvasY: canvasY,
            canvasWidth: canvasWidth,
            canvasHeight: canvasHeight
        };
    }

    /**
     * Update cursor based on position (grab when over selection, crosshair otherwise)
     */
    updateCursor(x: number, y: number): void {
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas) return;

        if (this.selectedArea && this.isPointInSelection(x, y)) {
            canvas.style.cursor = 'grab';
        } else {
            canvas.style.cursor = 'crosshair';
        }
    }

    /**
     * Draw final selection with solid border (different from preview)
     */
    drawFinalSelection(): void {
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!this.selectedArea) return;

        const { canvasX, canvasY, canvasWidth, canvasHeight } = this.selectedArea;

        // Solid green border for final selection
        ctx.strokeStyle = '#198754';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.strokeRect(canvasX, canvasY, canvasWidth, canvasHeight);

        // Semi-transparent green fill
        ctx.fillStyle = 'rgba(25, 135, 84, 0.15)';
        ctx.fillRect(canvasX, canvasY, canvasWidth, canvasHeight);

        // Draw move indicator icon in center (only in click mode)
        if (this.selectionMode === 'click') {
            const centerX = canvasX + canvasWidth / 2;
            const centerY = canvasY + canvasHeight / 2;

            // Draw 4 arrows indicating draggable
            ctx.fillStyle = 'rgba(25, 135, 84, 0.6)';
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('⊕', centerX, centerY);
        }
    }

    /**
     * Handler for mode change
     */
    onModeChange(): void {
        // Reset state when mode changes
        this.clearSelection();
        this.selection.isDrawing = false;
        this.isDragging = false;

        // Reset cursor
        const canvas = this.overlayCanvasRef?.nativeElement;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
        }
    }

    /**
     * Apply seal - return selection to parent
     */
    applySignature(): void {
        if (!this.selectedArea) {
            this.warningMessage = 'Va rugam selectati o zona pentru sigiliu.';
            return;
        }

        this.dialogRef.close({
            success: true,
            selection: this.selectedArea
        });
    }

    /**
     * Cancel and close
     */
    cancel(): void {
        this.dialogRef.close({
            success: false
        });
    }
}
